// @flow

import { useEffect, useCallback, useRef } from 'react';
import * as AvailableMetricsModel from '../../../models/available-metrics';
import * as WidgetPropsModel from '../../../models/widget-props';
import * as LocationModel from '../../../models/location';
import WidgetUI from '../../widgets-ui/map';
import { buildWidgetDateString } from '../common/stringHelpers';
import { useHasWidgetChanged, useSafeState, useSafeVisible } from '../common/hooks';
import { IS_EMAIL_VIEW } from '../../../config/vars';
import { formatNumbersWithUnits } from '../../../utils/formatNumbers';
import { processMapWidget } from '@auravisionlabs/aura-pkg-report-widgets/dist/core/widgets/map';
import { browserQueryAPI } from '../../../services/query-api-browser';
import { useWidgetRenderState } from '../../../hooks/use-widget-render-state';

const MapWidget = ({
  widget,
  availableMetrics,
  locations,
  uiOptions = {},
  reportWidgetId,
}: WidgetPropsModel.mapT) => {
  const [loading, setLoading] = useSafeState(false);
  const [error, setError] = useSafeState(false);
  const [autoTitle, setAutoTitle] = useSafeState('');
  const [widgetDescription, setWidgetDescription] = useSafeState('');
  const [unitType, setUnitType] = useSafeState('number');
  const [points, setPoints] = useSafeState([]);
  const [fetchRequired, setFetchRequired] = useSafeState(true);
  const hasInitialFetchedRef = useRef(false);
  const hasWidgetChanged = useHasWidgetChanged(widget);
  const [currentElement, isVisible] = useSafeVisible(
    (vi) => IS_EMAIL_VIEW || uiOptions.disableViewportLoading || vi > 0
  );
  const { reportLoading, reportComplete, reportError } = useWidgetRenderState(reportWidgetId);

  const title = widget.customTitle || autoTitle;

  const usingComps =
    !!widget.comparisonPeriod &&
    widget.comparisonPeriod.active &&
    widget.showChangeValue;

  const loadWidgetData = useCallback(
    async (metric) => {
      setLoading(true);
      setError(false);
      reportLoading();

      try {
        const config = {
          metric: widget.metric,
          period: widget.period,
          comparisonPeriod: widget.comparisonPeriod,
          locations: widget.locations,
          demographicFilter: metric.demographicFilter,
          showChangeValue: widget.showChangeValue,
          ...(widget.customTitle && { customTitle: widget.customTitle }),
        };

        const deps = {
          queryAPI: browserQueryAPI,
          availableMetrics: availableMetrics,
          locations: locations,
        };

        const widgetData = await processMapWidget(config, deps);

        // Use title from package
        setAutoTitle(widgetData.title);

        // Extract unit type from first row if available
        if (widgetData.rows.length > 0 && widgetData.rows[0].length > 1) {
          setUnitType(widgetData.rows[0][1].unit);
        }

        // Map WidgetData back to points format for the UI
        const builtPoints = widgetData.rows.map((row) => {
          // row is [Location, Value, Change?]
          const locationCell = row[0];
          const valueCell = row[1];
          const changeCell = row[2];

          // Get lat/lng from locations list
          const locationId = widget.locations.find(
            (id) => LocationModel.getLocationName(id, locations) === locationCell.value
          );
          const latLng = locationId ? LocationModel.getLocationLatLng(locationId, locations) : { lat: null, lng: null };

          return {
            label: locationCell.value,
            value: valueCell.value,
            changeValue: changeCell ? changeCell.value : null,
            lat: latLng.lat,
            lng: latLng.lng,
          };
        });

        setPoints(builtPoints);

        // Report completion to context - use package output directly
        reportComplete(widgetData);
      } catch (err) {
        console.error('Error loading Map widget:', err);
        setError(true);
        reportError(err.message || 'Failed to load Map widget');
      } finally {
        setLoading(false);
      }
    },
    [
      availableMetrics,
      locations,
      widget.comparisonPeriod,
      widget.customTitle,
      widget.locations,
      widget.metric,
      widget.period,
      widget.showChangeValue,
    ]
  );

  useEffect(() => {
    if (hasWidgetChanged && hasInitialFetchedRef.current) setFetchRequired(true);
  }, [hasWidgetChanged]);

  useEffect(() => {
    if (fetchRequired && isVisible) {
      setFetchRequired(false);
      hasInitialFetchedRef.current = true;

      // get the information from available metrics data for the metric
      const metric = {
        ...widget.metric, // Metric-level properties
        demographicFilter: widget.metric.demographicFilter || widget.demographicFilter, // Metric-level takes precedence, fallback to widget-level
        details: AvailableMetricsModel.findMetricDetails(
          availableMetrics,
          widget.metric.metricGroupKey,
          widget.metric.metricKey
        ),
      };

      if (!metric || !metric.details) {
        console.error('No metric found in availableMetrics for the Map widget');
        setError(true);
      } else {
        // Set description only (title is now generated by the package)
        if (!uiOptions.disableDescription) {
          const demos = Object.values(metric.demographicFilter ?? {}).filter(Boolean).join(" ");
          const desc = AvailableMetricsModel.getMetricDescription(metric.details);
          setWidgetDescription(demos.length? `${desc} (${demos})` : desc);
        }
        loadWidgetData(metric);
      }
    }
  }, [widget, fetchRequired, isVisible, loadWidgetData, availableMetrics, uiOptions.disableDescription]); // eslint-disable-line react-hooks/exhaustive-deps

  const subtitle = buildWidgetDateString(widget, true);

  return (
    <div ref={currentElement}>
      <WidgetUI
        errors={error}
        loading={loading}
        title={title}
        subtitle={subtitle}
        description={widgetDescription}
        points={points}
        unitType={unitType}
        dataKey={widget.showChangeValue ? 'changeValue' : 'value'}
      />
    </div>
  );
};

export default MapWidget;
